import {
  Internship,
  Scholarship,
  CareerPath,
  ContentNugget,
  CareerPathScoreSection,
  CareerPathResponse,
  College,
  AppIcons,
  SenderType,
  CareerQuestionFacts,
  Major,
  Industry,
  Employer,
  Coach,
  DeepDive,
  NuggetVariant,
  PriorityPlaylist,
  SearchResult,
} from '@backrs_ui/common-ui';

import { ToolId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { UserDocuments } from '@backrs_ui/grow-ui/src/redux/user/user.lubav.api';
import { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

export const SCHOOL_LEVEL = {
  HIGH_SCHOOL: 'high_school',
  COLLEGE: 'college',
  TRADE: 'trade',
  NO_CURRENT_ENROLLMENT: 'no_current_enrollment',
  GRADUATE_SCHOOL: 'graduate_school',
  COMMUNITY_COLLEGE: 'community_college',
} as const;

export type SchoolLevel = (typeof SCHOOL_LEVEL)[keyof typeof SCHOOL_LEVEL];

type CareerQuestionLogic = Readonly<{
  /** which question are we targeting */
  logic_question_id: number;
  /** which question is this logic associated with */
  career_question_id: number;
  career_question_option_id: number;
  action: 'HIDE' | 'SHOW';
}>;

export type CareerQuestion = Readonly<{
  id: number;
  calculator_key: string;
  title: string;
  question: string;
  facts?: CareerQuestionFacts[];
  aspirational_title?: string;
  sequence: number;
  is_multi_select?: boolean;
  is_custom_response?: boolean;
  school_level: SchoolLevel | 'both';
  conditional_logic?: CareerQuestionLogic[];
  options: CareerQuestionOption[];
}>;

type CareerQuestionOption = Readonly<{
  id: number;
  calculator_key: string;
  career_question_id: number;
  answer: string;
  sequence: number;
  point_value?: number;
  aspirational_title?: string;
  is_active?: boolean;
}>;

export type CareerUserResponseSet = Readonly<{
  id: string; // uuid
  user_responses: CareerUserResponseAnswer[];
  school_level?: SchoolLevel;
  created_at?: string;
  ownership_level?: OwnershipLevel;
  zip_code?: string;
  graduation_year?: string;
  school?: string;
  start_term?: string;
  start_year?: string;
}>;

export type CareerUserResponseAnswer = Readonly<{
  id: string; // uuid
  career_user_response_set_id: string; // uuid
  career_question_id: number;
  career_question_option_ids: number[];
  career_question_option_custom_text?: string;
}>;

export type CareerPathWithScore = Readonly<{
  careerPath: CareerPath;
  sum: number;
  scoreSection: CareerPathScoreSection;
}>;

export type CareerRole = Readonly<{
  id: number;
  name: string;
}>;

export type CareerPathRole = Readonly<{
  id: number;
  career_role_id: number;
  career_path_id: number;
  tier: 'trade' | 'middle' | 'upper' | 'top';
}>;

export type CareerPathEnjoyment = Readonly<{
  id: number;
  career_path_id: number;
  working_with_hands_value: number;
  creating_value: number;
  promoting_or_selling_value: number;
  serving_or_caring_value: number;
  coaching_teaching_advising_value: number;
  analyzing_and_solving_problems_value: number;
  collaborating_value: number;
  making_decisions_value: number;
  following_a_playbook_value: number;
  leading_people_value: number;
}>;

export type CareerPathMeaning = Readonly<{
  id: number;
  career_path_id: number;
  relationships_value: number;
  legacy_value: number;
  personal_growth_value: number;
  social_impact_value: number;
  innovation_value: number;
  connection_to_nature_value: number;
  creativity_value: number;
  benefiting_others_value: number;
  honoring_spiritual_beliefs_value: number;
}>;

export const LUBAV_SUPERSESSIONS = {
  SUPERCOACH: 'supercoach',
  SUPERUSER: 'superuser',
  OFFICE_HOURS: 'office_hours',
};

export type LubavSupersession = (typeof LUBAV_SUPERSESSIONS)[keyof typeof LUBAV_SUPERSESSIONS];

export const OFFICE_HOURS_SESSION_IDS = {
  MAJORS: 'majors',
  ESSAY_IDEAS: 'essay_ideas',
  LETTERS_OF_REC: 'letters_of_rec',
  WHAT_ARE_YOUR_CHANCES: 'what_are_your_chances',
  BOOST_YOUR_PROFILE: 'boost_your_profile',
  BOOST_ODDS_OF_AN_OFFER: 'boost_odds_of_an_offer',
  RESUME_OPTIMIZER: 'resume_optimizer',
  LOWER_COST_OF_COLLEGE: 'lower_cost_of_college',
  JOBS_YOU_MIGHT_LOVE: 'jobs_you_might_love',
  COLLEGE_SHORTLIST: 'college_shortlist',
  IMPROVE_YOUR_TEST_SCORES: 'improve_your_test_scores',
} as const;

export const STRUCTURED_SESSION_IDS = Object.values(OFFICE_HOURS_SESSION_IDS);

export type StructuredSessionId =
  (typeof OFFICE_HOURS_SESSION_IDS)[keyof typeof OFFICE_HOURS_SESSION_IDS];

export const isStructuredSession = (
  sessionId: string | null | undefined,
): sessionId is StructuredSessionId =>
  !!sessionId && (STRUCTURED_SESSION_IDS as readonly string[]).includes(sessionId);

export type SessionStatus = 'none' | 'mid' | 'complete';

export const OFFICE_HOURS_SUBSESSION_IDS = {
  // High school
  HIGH_SCHOOL_ACT_SAT: 'high_school_act_sat',
  HIGH_SCHOOL_COLLEGE_MAJORS: 'high_school_college_majors',
  HIGH_SCHOOL_UNIVERSITIES: 'high_school_universities',
  HIGH_SCHOOL_SCHOLARSHIP: 'high_school_scholarship',
  HIGH_SCHOOL_GRADES: 'high_school_grades',
  HIGH_SCHOOL_RESUME: 'high_school_resume',
  HIGH_SCHOOL_CAREERS: 'high_school_careers',
  // College
  COLLEGE_INTERNSHIPS: 'college_internships',
  COLLEGE_GRADES: 'college_grades',
  COLLEGE_RESUME: 'college_resume',
  COLLEGE_CAREERS: 'college_careers',
  COLLEGE_MAJORS: 'college_majors',
  COLLEGE_SCHOLARSHIP: 'college_scholarship',
  // Guest (stage unknown, no profile)
  GUEST_CAREERS: 'guest_careers',
  GUEST_MAJORS: 'guest_majors',
  GUEST_COLLEGES: 'guest_colleges',
  GUEST_RESUME: 'guest_resume',
  GUEST_GRADES: 'guest_grades',
  GUEST_INTERNSHIPS: 'guest_internships',
  GUEST_SCHOLARSHIP: 'guest_scholarship',
} as const;

export type LubavOfficeHoursSubsessionType =
  (typeof OFFICE_HOURS_SUBSESSION_IDS)[keyof typeof OFFICE_HOURS_SUBSESSION_IDS];

export const SOLUTION_SESSION_IDS = {
  RESUME_BUILDER: 'resume_builder',
  RESUME_BUILDER_EDUCATION: 'resume_builder.education',
  RESUME_BUILDER_WORK_EXPERIENCE: 'resume_builder.work_experience',
  RESUME_BUILDER_LEADERSHIP: 'resume_builder.leadership',
  RESUME_BUILDER_SKILLS: 'resume_builder.skills',

  DEFAULT: 'default',
} as const;

export type SolutionSessionId = (typeof SOLUTION_SESSION_IDS)[keyof typeof SOLUTION_SESSION_IDS];
export type LubavSessionType = LubavSupersession | StructuredSessionId | SolutionSessionId;

// Display titles for the supersessions which do not have associated tool records
export const SUPERSESSION_TITLES: Partial<Record<string, string>> = {
  [LUBAV_SUPERSESSIONS.SUPERCOACH]: 'Super Coach',
  [LUBAV_SUPERSESSIONS.SUPERUSER]: 'Super User',
  [LUBAV_SUPERSESSIONS.OFFICE_HOURS]: 'Office Hours',
};

export type LubavCompetency = Readonly<{
  id: string;
  relevance?: string;
  metadata?: {
    id: number;
    description: string;
    title: string;
    relevance_score?: number;
    slug: string;
    media_url?: string;
  };
}>;

export type LubavInternship = Readonly<{
  id: string;
  relevance?: string;
  metadata?: Internship & { relevance_score?: number };
}>;

export type LubavPost = Readonly<{
  id: string;
  relevance?: string;
  quote: string;
  quote_author: string;
  metadata: {
    author: string;
    created_at: string;
    author_profile_picture: string;
    relevance_score?: number;
  };
}>;

export type LubavResource = Readonly<{
  id: string;
  relevance?: string;
  metadata?: ContentNugget & { relevance_score?: number };
}>;

export type LubavCareerPath = Readonly<{
  id: string;
  relevance?: string;
  metadata?: CareerPathResponse & { relevance_score?: number };
}>;

export type LubavTool = Readonly<{
  id: ToolId;
  relevance?: string;
  metadata?: {
    relevance_score?: number;
  };
}>;

export type LubavCollege = Readonly<{
  id: string | undefined;
  description?: string;
  relevance?: string;
  metadata?: College & { relevance_score?: number };
}>;

export const RESOURCE_KEY = {
  RESOURCES: 'resources',
  COMPETENCIES: 'competencies',
  SCHOLARSHIPS: 'scholarships',
  INTERNSHIPS: 'internships',
  TOOLS: 'tools',
  CAREER_PATHS: 'career_paths',
  COLLEGES: 'colleges',
  LEARNING: 'learning',
  STORIES: 'stories',
  MAJORS: 'majors',
  INDUSTRIES: 'industries',
  EMPLOYERS: 'employers',
  SUGGESTED_PLAYBOOKS: 'suggested_playbooks',
  COACHES: 'coaches',
  DEEP_DIVES: 'deep_dives',
} as const;

export type ResourceKey = (typeof RESOURCE_KEY)[keyof typeof RESOURCE_KEY];

// Sessions whose primary output is a set of nuggets rather than a generated artifact, and the
// nugget types to feature for each.
export const NUGGET_OUTPUT_SESSION_RESOURCES: Partial<Record<StructuredSessionId, ResourceKey[]>> =
  {
    [OFFICE_HOURS_SESSION_IDS.MAJORS]: [RESOURCE_KEY.MAJORS],
    [OFFICE_HOURS_SESSION_IDS.LOWER_COST_OF_COLLEGE]: [RESOURCE_KEY.SCHOLARSHIPS],
    [OFFICE_HOURS_SESSION_IDS.JOBS_YOU_MIGHT_LOVE]: [RESOURCE_KEY.INTERNSHIPS],
  };

export const getNuggetOutputResources = (sessionId: string | null | undefined): ResourceKey[] =>
  (sessionId && NUGGET_OUTPUT_SESSION_RESOURCES[sessionId as StructuredSessionId]) || [];

export type ResourcesContent = Readonly<{
  [RESOURCE_KEY.RESOURCES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavResource[] };
  };
  [RESOURCE_KEY.COMPETENCIES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavCompetency[] };
  };
  [RESOURCE_KEY.SCHOLARSHIPS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavScholarship[] };
  };
  [RESOURCE_KEY.INTERNSHIPS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavInternship[] };
  };
  [RESOURCE_KEY.TOOLS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavTool[] };
  };
  [RESOURCE_KEY.CAREER_PATHS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavCareerPath[] };
  };
  [RESOURCE_KEY.COLLEGES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavCollege[] };
  };
  [RESOURCE_KEY.STORIES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavResource[] };
  };
  [RESOURCE_KEY.LEARNING]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavResource[] };
  };
  [RESOURCE_KEY.MAJORS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavMajor[] };
  };
  [RESOURCE_KEY.INDUSTRIES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavIndustry[] };
  };
  [RESOURCE_KEY.EMPLOYERS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavEmployer[] };
  };
  [RESOURCE_KEY.SUGGESTED_PLAYBOOKS]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavSuggestedPlaybook[] };
  };
  [RESOURCE_KEY.COACHES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavCoach[] };
  };
  [RESOURCE_KEY.DEEP_DIVES]?: {
    relevance?: Record<string, number>[];
    content: { content: LubavDeepDive[] };
  };
}>;

export type ResourcesContentItem =
  | LubavResource
  | LubavCompetency
  | LubavInternship
  | LubavTool
  | LubavCareerPath
  | LubavCollege
  | LubavMajor
  | LubavIndustry
  | LubavEmployer
  | LubavSuggestedPlaybook
  | LubavCoach
  | LubavDeepDive;

export type DraggableNuggetData = Readonly<{
  id: string;
  type: string;
  title: string;
}>;

// the attach lookups for these are keyed by slug/code, not numeric id
const SLUG_KEYED_RESOURCE_KEYS: ReadonlySet<ResourceKey> = new Set<ResourceKey>([
  'tools',
  'competencies',
  'majors',
]);

export const getDraggableNuggetId = (nugget: ContentNugget, resourceKey: ResourceKey): string =>
  SLUG_KEYED_RESOURCE_KEYS.has(resourceKey) && nugget.slug ? nugget.slug : String(nugget.id);

// resource key for the chat drag payload; variants without one aren't draggable
export const VARIANT_TO_RESOURCE_KEY: Partial<Record<NuggetVariant, ResourceKey>> = {
  tool: 'tools',
  playlist: 'suggested_playbooks',
  competency: 'competencies',
  internship: 'internships',
  'career-path': 'career_paths',
  scholarships: 'scholarships',
  employer: 'employers',
  industry: 'industries',
  institution: 'colleges',
  major: 'majors',
  coach: 'coaches',
  'deep-dive': 'deep_dives',
  article: 'learning',
  'video-external': 'learning',
  'video-internal': 'learning',
  book: 'learning',
  audio: 'learning',
  document: 'learning',
};

type StudyPlanWeek = Readonly<{
  goals: string[];
  monday?: string[];
  tuesday?: string[];
  wednesday?: string[];
  thursday?: string[];
  friday?: string[];
  saturday?: string[];
  sunday?: string[];
}>;

type StudyPlan = Record<string, StudyPlanWeek>;

type CollectedDataBox = Readonly<{
  name: string[];
  value: string[];
}>;

export type CollectedData = Readonly<{
  box_1: CollectedDataBox;
  box_2: CollectedDataBox;
  hidden: CollectedDataBox;
}>;

export type School = Readonly<{
  id: string;
  relevance: string;
  highlights: string;
  considerations: string;
  metadata: College;
}>;

export type LubavScholarship = Readonly<{
  id: number;
  relevance?: string;
  metadata?: Scholarship & { relevance_score?: number };
}>;

export type LubavMajor = Readonly<{
  id: string;
  relevance?: string;
  metadata?: Major & { relevance_score?: number };
}>;

export type LubavIndustry = Readonly<{
  id: string;
  relevance?: string;
  metadata?: Industry & { relevance_score?: number };
}>;

export type LubavEmployer = Readonly<{
  id: string;
  relevance?: string;
  metadata?: Employer & { relevance_score?: number };
}>;

export type LubavSuggestedPlaybook = Readonly<{
  id: string;
  relevance?: string;
  metadata?: PriorityPlaylist & { relevance_score?: number };
}>;

export type LubavCoach = Readonly<{
  id: string;
  relevance?: string;
  metadata?: Coach & { relevance_score?: number };
}>;

export type LubavDeepDive = Readonly<{
  id: string;
  relevance?: string;
  metadata?: DeepDive & { relevance_score?: number };
}>;

export type Job = Readonly<{
  id: number;
  benefits: string;
  goals_fit: string;
  goals_discrepency: string;
  qualified: string;
  not_qualified: string;
}>;

export type MainResponse = Readonly<{
  safe: { schools: School[] };
  target: { schools: School[] };
  stretch: { schools: School[] };
  scholarships?: { scholarships: LubavScholarship[] };
  study_plan?: { plan: StudyPlan; priorities: { priorities: string[] } };
  opportunities?: { opportunities: Job[] };
  letter?: { letter: string };
  action_plan?: { action_plan: string };
  user_commonalities?: {
    id: string;
    relevance: string;
    insights: string[];
    questions: string[];
    commonalities: string;
  };
  what_are_your_chances?: { what_are_your_chances: string };
  resume_optimizer?: { resume_optimizer: string };
  boost_your_profile?: { boost_your_profile: string };
  boost_odds_of_an_offer?: { boost_odds_of_an_offer: string };
  resume_builder?: { resume_builder: string };
  final_message?: string;
  suggested_prompts?: string[];
  tool_ids?: string[];
  job_opportunity_ids?: string[];
  competency_ids?: string[];
  story_ids?: string[];
  skill_ids?: string[];
  college_ids?: string[];
  career_path_ids?: string[];
  scholarship_ids?: string[];
}>;

type FinalMessage = Readonly<{
  text: string;
  question: string | null;
  finished: boolean;
  query_id: number;
  response_id: number;
  ask_to_regenerate: boolean;
}>;

export type BoxData = Readonly<{
  mainResponse?: MainResponse;
  resources?: ResourcesContent;
  collectedData?: CollectedData;
  finalMessage?: FinalMessage;
}>;

export type SearchedUser = Readonly<{
  id: string;
  name: string;
  relevance: number;
  profile_image?: string;
  documents?: UserDocuments;
}>;

export const MESSAGE_MODE = {
  QA: 'qa',
  CHAT: 'chat',
} as const;

export type MessageMode = (typeof MESSAGE_MODE)[keyof typeof MESSAGE_MODE];

export type Contact = Readonly<{
  id: string;
  relevance: string;
  metadata: {
    name: string;
    role: 'professional' | 'student' | 'foundations';
    school: string | null;
    job_title: string;
    location: string;
    employer?: string;
    link: string;
    profile_type?: 'external' | 'backr';
    profile_image?: string;
    profile_cover_image?: string;
  };
}>;

export type AttachedNuggetData = Readonly<{
  id: string;
  type: string;
  title: string;
}>;

export type AttachedFileData = Readonly<{
  // A file attachment has an s3_key; a link attachment has a source_url instead.
  s3_key?: string;
  filename: string;
  mime_type: string;
  source_url?: string;
}>;

export type BaseMessage = Readonly<{
  id?: string;
  text?: string;
  sender: SenderType;
  componentInsideMessage?: boolean;
  componentType?:
    | { type: 'SELECTED_USER'; user: SearchedUser }
    | {
        type: 'FILE_UPLOAD';
        documentType?: DocumentType;
        allowSkipDocument?: boolean;
        includeFreeform?: boolean;
      }
    | { type: 'CONFIRM_DOCUMENT'; documentType?: DocumentType; includeFreeform?: boolean }
    | { type: 'SINGLE_SELECT'; options: string[]; includeFreeform?: boolean; required?: boolean }
    | {
        type: 'MULTI_SELECT';
        options: string[];
        exclusiveOptions?: string[];
        includeFreeform?: boolean;
        required?: boolean;
      }
    | {
        type: 'MULTI_NUMERIC';
        options: string[];
        required?: boolean;
      }
    | { type: 'NUMERIC'; includeFreeform?: boolean; required?: boolean }
    | { type: 'CAREER_PATH_SELECTOR'; includeFreeform?: boolean; required?: boolean }
    | { type: 'COLLEGE_TYPE_SELECTOR'; includeFreeform?: boolean; required?: boolean }
    | {
        type: 'USER_SUGGESTION';
        users: { id: string; relevance?: string }[];
        includeFreeform?: boolean;
        required?: boolean;
      }
    | { type: 'SKIP_ONLY' }
    | { type: 'ATTACHED_NUGGETS'; nuggets: AttachedNuggetData[] }
    | { type: 'ATTACHED_FILES'; attachments: AttachedFileData[] }
    | { type: 'ERROR' };
}>;

export const CHART_TYPES = ['column', 'line', 'donut', 'stacked_bar', 'heat_grid'] as const;

export type ChartType = (typeof CHART_TYPES)[number];

export type ChartSeries = Readonly<{
  name: string;
  values: number[];
}>;

// A chart Lubav drew: a table whose type picks the renderer.
export type ChartSpec = Readonly<{
  type: ChartType;
  title: string;
  categories: string[];
  series: ChartSeries[];
  x_label: string | null;
  y_label: string | null;
}>;

export type OfficeHoursMessage = BaseMessage &
  Readonly<{
    nuggets?: {
      users: Contact[]; // other nugget types may be added
    };
    timestamp?: string;
    query_id?: number;
    response_id?: number;
    followUpButtons?: FollowUpButton[];
    charts?: ChartSpec[];
  }>;

export type SessionSwitchAction = 'start' | 'continue' | 'restart' | 'discuss';

export type SessionSwitchChoice = Readonly<{
  targetSession: LubavSessionType;
  action: SessionSwitchAction;
}>;

export type FollowUpButton = Readonly<{
  id: string;
  label: string;
  promptButtonId?: string;
  prompt?: string;
  sessionSwitch?: SessionSwitchChoice;
}>;

export type SolutionSessionMessageType =
  | 'freeform'
  | 'numeric'
  | 'single_select'
  | 'multi_select'
  | 'file_upload'
  | 'multi_freeform'
  | 'multi_numeric'
  | 'user_suggestion'
  | 'final_message'
  | 'education'
  | 'work_experience'
  | 'engagement';

export type ResumeBuilderStep = 'basic_information' | 'digging_for_gold' | 'polish';

export type SolutionSessionMessage = BaseMessage &
  Readonly<{
    mode?: MessageMode;
    additionalText?: string;
    question?: string;
    questionField?: string;
    field?: string;
    type?: SolutionSessionMessageType;
    options?: string[];
    include_freeform?: boolean;
    required?: boolean;
    user_selection_options?: Contact[];
    placeholder?: string;
    ask_to_regenerate?: boolean;
    suggestedPrompts?: string[];
    hasUpdatedForDocument?: boolean;
    userChoseNewUpload?: boolean;
    documents_requested?: string[];
    update_on_submit?: boolean;
    resume_builder_step?: ResumeBuilderStep;
    validation_feedback?: string;
    response_is_valid?: boolean;
  }>;

export const APP_LANGUAGES = {
  ENGLISH: 'english',
  SPANISH: 'spanish',
  FRENCH: 'french',
} as const;

export type AppLanguage = (typeof APP_LANGUAGES)[keyof typeof APP_LANGUAGES];

export const LUBAV_LLM = 'flagship';

export const DOCUMENT_TYPES = {
  RESUME: 'resume',
  TRANSCRIPT: 'transcript',
  PERSONAL_ASSESSMENT: 'personal_assessment',
  OTHER: 'other',
} as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

export type ProfileItemType = (typeof PROFILE_ITEM_TYPES)[keyof typeof PROFILE_ITEM_TYPES];

export const PROFILE_ITEM_TYPES = {
  RESUME: 'resume',
  TRANSCRIPT: 'transcript',
  PERSONAL_ASSESSMENT: 'personal_assessment',
  OTHER_DOCUMENTS: 'other_documents',
  BLUEPRINT: 'blueprint',
  LIFE_READINESS: 'life_readiness',
  TRACK_ASSESSMENT: 'track_assessment',
  ...OFFICE_HOURS_SESSION_IDS,
  ...SOLUTION_SESSION_IDS,
} as const;

export const DOCUMENT_NAMES: Record<DocumentType, string> = {
  [DOCUMENT_TYPES.RESUME]: 'resume',
  [DOCUMENT_TYPES.TRANSCRIPT]: 'transcript',
  [DOCUMENT_TYPES.PERSONAL_ASSESSMENT]: 'document', // this can be a wide variety of documents, so we'll just call it a document
  [DOCUMENT_TYPES.OTHER]: 'document',
};

export type PromptTag = 'Ask' | 'Follow up' | 'Connect' | 'Pursue' | 'Learn' | 'Assess' | 'Explore';

export type PromptItem = Readonly<{
  display: string;
  input?: string;
  hidden?: boolean;
  requireDocument?: DocumentType[];
  allowSkipDocument?: boolean;
  includeProfileItems?: ProfileItemType[];
  tag?: PromptTag;
}>;

export type CareerPathSearchCategory = SearchCategory;

export type CareerPathCategory = Readonly<{
  key: CareerPathSearchCategory;
  label: string;
  isParent?: boolean;
  clearsOtherFilters?: boolean;
  subCategories?: CareerPathCategory[];
}>;

export type CareerPathSearchParentCategory = Readonly<{
  name: string;
  iconName: AppIcons;
  isInMoreFilters: boolean;
  categories: CareerPathCategory[];
}>;

export type CareerPathSearchResult = SearchResult;

export type CareerPathSearchParams = Readonly<{
  searchTerm?: string;
  selectedFilters: CareerPathSearchCategory[];
  searchResults?: CareerPathSearchResult;
  hasInitialSearchBeenMade: boolean;
  initialResultCount?: number;
}>;
